<template>
    <div>
        <div
            v-for="token in tokens"
            :key="token.id"
            class="mb-10 w-full rounded-[6px] bg-white shadow-card"
        >
            <div class="px-6 pb-6 pt-4">
                <div
                    class="mb-7 flex flex-wrap items-center justify-between sm:flex-nowrap"
                >
                    <h4 class="font-medium leading-normal text-gray-800">
                        {{ token.name }}
                    </h4>

                    <AtomicButton
                        color="white"
                        size="sm"
                        data-cy="plugin-settings-remove-token"
                        @click="$emit('selectModal', token.id)"
                    >
                        Remove token
                    </AtomicButton>
                </div>

                <div
                    v-if="token.bucket_blocked_at"
                    class="mb-4 rounded-[4px] bg-yellow-100 px-[14px] py-3"
                >
                    <div class="flex flex-col items-start sm:flex-row">
                        <ExclamationTriangleIcon
                            class="mr-[14px] h-5 w-5 shrink-0 text-yellow-600"
                        />
                        <div class="mt-2 sm:mt-0">
                            <div class="text-sm font-medium text-yellow-800">
                                Storage blocked
                            </div>
                            <div class="mt-[7px] text-sm text-yellow-700">
                                You won't be able to use images in your
                                templates. Please contact support at
                                <a
                                    class="font-medium"
                                    href="mailto:support@topol.io"
                                    >support@topol.io</a
                                >.
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="
                        token?.settings?.storage_provider?.includes(
                            'disabled'
                        ) && !token.bucket_blocked_at
                    "
                    class="mb-4 rounded-[4px] bg-yellow-100 px-[14px] py-3"
                >
                    <div class="flex flex-col items-start sm:flex-row">
                        <ExclamationTriangleIcon
                            class="mr-[14px] h-5 w-5 shrink-0 text-yellow-600"
                        />
                        <div class="mt-2 sm:mt-0">
                            <div class="text-sm font-medium text-yellow-800">
                                Custom storage disabled
                            </div>
                            <div class="mt-[7px] text-sm text-yellow-700">
                                You are not allowed to have custom storage
                                enabled for this token. You need a Plugin
                                subscription (Plugin Expansion or higher) to use
                                this feature.
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="flex flex-wrap items-start justify-between sm:flex-nowrap"
                >
                    <div
                        class="mb-4 mr-2 flex min-w-[180px] flex-col text-sm leading-normal sm:mb-0"
                    >
                        <p class="text-gray-500">Last active</p>
                        <p
                            v-if="token.last_used_at"
                            class="font-medium text-gray-800"
                        >
                            {{
                                format(
                                    new Date(token.last_used_at),
                                    "hh:mm:ss a '&nbsp;' d'&nbsp;'LLL,'&nbsp;'yyyy"
                                )
                            }}
                        </p>
                        <p v-else>Never</p>

                        <button
                            type="button"
                            class="justify-left mt-[3px] flex font-medium text-topol-blue-500 hover:text-topol-blue-600"
                            @click="$emit('showCopyTokenModal', token.id)"
                        >
                            Show API keys
                        </button>

                        <inertia-link
                            v-if="
                                globalProps.team.owner_id ===
                                globalProps.user.id
                            "
                            href="/billing/invoices"
                            ><p
                                class="mt-[19px] font-medium text-topol-blue-500 hover:text-topol-blue-600"
                            >
                                See invoices
                            </p></inertia-link
                        >

                        <inertia-link
                            v-if="
                                globalProps.team.owner_id ===
                                globalProps.user.id
                            "
                            href="/billing/plugin-subscriptions"
                            ><p
                                class="mt-[3px] font-medium text-topol-blue-500 hover:text-topol-blue-600"
                            >
                                {{
                                    isPluginSubscribed
                                        ? 'Your active subscription'
                                        : 'Get your subscription'
                                }}
                            </p></inertia-link
                        >
                    </div>

                    <div
                        class="relative mb-4 mr-2 flex min-w-[180px] flex-col text-sm leading-normal sm:mb-0"
                    >
                        <p class="text-gray-500">Data traffic</p>

                        <a
                            :href="
                                route('usage-statistics', {
                                    apiToken: token.id,
                                })
                            "
                            target="_blank"
                        >
                            <p
                                class="font-medium text-topol-blue-500 hover:text-topol-blue-600"
                            >
                                See data traffic details
                            </p>
                        </a>
                    </div>

                    <div
                        class="relative mb-4 mr-2 flex min-w-[180px] flex-col text-sm leading-normal sm:mb-0"
                    >
                        <p class="text-gray-500">OpenAI Text Assistant</p>

                        <p
                            class="cursor-pointer font-medium text-topol-blue-500 hover:text-topol-blue-600"
                            @click="$emit('openAISettingsModal', token.id)"
                        >
                            Edit your settings
                        </p>
                    </div>
                </div>
                <TokenSettings
                    :key="token.id"
                    :token="token"
                    @show-gcs-modal="$emit('showGcsModal', token.id)"
                    @show-aws-modal="$emit('showAwsModal', token.id)"
                    @show-cloudflare-modal="
                        $emit('showCloudflareModal', token.id)
                    "
                    @show-digital-ocean-modal="
                        $emit('showDigitalOceanModal', token.id)
                    "
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import AtomicButton from '@atomic/Buttons/AtomicButton.vue';
import { format } from 'date-fns';
import { PropType } from 'vue';
import IToken from '@/types/Plugin/IToken';
import TokenSettings from './TokenSettings.vue';
import { route } from 'ziggy-js';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid';

defineProps({
    tokens: {
        type: Array as PropType<IToken[]>,
        default: () => [],
    },
});

defineEmits([
    'selectModal',
    'showGcsModal',
    'showAwsModal',
    'showCloudflareModal',
    'showDigitalOceanModal',
    'openAISettingsModal',
    'showCopyTokenModal',
]);

const { isPluginSubscribed } = useSubscriptions();

const globalProps = computed(() => usePage<ITopolPageProps>().props);
</script>

<style></style>
